"use client";

import { useMemo } from "react";
import AddBpMeasurement from "@/components/measurements/add-bp-measurement";
import AddMeasurementsDialog from "@/components/measurements/add-measurements-dialog";
import AddWeightMeasurement from "@/components/measurements/add-weight-measurement";
import Button from "@/components/ui/button";
import { useLocationClock } from "@/hooks/use-clinic-clock";
import { formatBp, formatWeight } from "@/lib/format-data";
import type { AppointmentChecklistItem, EventDetail } from "@/types/event";
import type { PatientChart } from "@/types/patient";
import { appointmentDayLabel } from "./appointment-day-label";
interface DefaultMeasurementChecklistActionProps {
  item: AppointmentChecklistItem;
  event: EventDetail;
  patientId: string;
  patient?: PatientChart | null;
  canUpdateItem: boolean;
}
export function DefaultMeasurementChecklistAction({
  item,
  event,
  patientId,
  patient,
  canUpdateItem
}: DefaultMeasurementChecklistActionProps) {
  // Readings belong to the appointment's day on its location's clock.
  const eventClock = useLocationClock(event.location?.id);
  const appointmentDay = appointmentDayLabel(event.start, eventClock);
  const todaysMeasurements_0 = useMemo(() => {
    if (!patient?.measurements || patient.measurements.length === 0) {
      return {
        weight: null,
        bloodPressure: null
      };
    }

    // Readings on the appointment's clinic day.
    const eventDay = eventClock.dayOf(event.start);
    const todaysMeasurements = patient.measurements.filter(m => eventClock.dayOf(m.measurementDate) === eventDay);

    // Sort by created date, most recent first
    const sortedMeasurements = [...todaysMeasurements].sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());

    // Get the most recent weight and blood pressure measurements
    const latestWeight = sortedMeasurements.find(m_0 => m_0.weight !== undefined && m_0.weight !== null);
    const latestBloodPressure = sortedMeasurements.find(m_1 => m_1.systolicBp !== undefined && m_1.systolicBp !== null && m_1.diastolicBp !== undefined && m_1.diastolicBp !== null);
    return {
      weight: latestWeight,
      bloodPressure: latestBloodPressure
    };
  }, [patient?.measurements, event.start, eventClock]);
  const {
    weight,
    bloodPressure
  } = todaysMeasurements_0;
  return <div className="flex flex-col">
      <div className="my-1 flex flex-col rounded-md border px-3 py-2">
        <p className="text-caption font-medium text-foreground-secondary">
          {appointmentDay ? `Vital Measurements taken on ${appointmentDay}` : "Today's Vital Measurements"}
        </p>
        {weight && weight.weight !== undefined ? <p className="text-caption text-foreground-secondary">
            Weight: {formatWeight(weight.weight)}
          </p> : <p className="text-caption text-muted-foreground">
            Weight: Not measured today
          </p>}

        {bloodPressure && bloodPressure.systolicBp && bloodPressure.diastolicBp ? <p className="text-caption text-foreground-secondary">
            BP: {formatBp(bloodPressure.systolicBp, bloodPressure.diastolicBp)}
          </p> : <p className="text-caption text-muted-foreground">
            Blood Pressure: Not measured today
          </p>}
      </div>

      {!item.completedDate && <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div>
              <AddWeightMeasurement patientId={patientId} selectedEventId={event.id}>
                <Button variant="secondary" size="sm" className="w-full" disabled={!canUpdateItem || !patientId}>
                  Add Weight
                </Button>
              </AddWeightMeasurement>
            </div>

            <div>
              <AddBpMeasurement patientId={patientId} selectedEventId={event.id}>
                <Button variant="secondary" size="sm" className="w-full" disabled={!canUpdateItem || !patientId}>
                  Add Blood Pressure
                </Button>
              </AddBpMeasurement>
            </div>
          </div>

          <AddMeasurementsDialog patientId={patientId} eventId={event.id}>
            <Button variant="secondary" size="sm" className="w-full" disabled={!canUpdateItem || !patientId}>
              Add Other Measurement
            </Button>
          </AddMeasurementsDialog>

          {!canUpdateItem && <p className="text-caption text-muted-foreground">
              You don&apos;t have permission to record checklist measurements.
            </p>}
        </div>}
    </div>;
}